import { useEffect, useState } from "react";
import { ApiError, type PortalSignableDocument } from "@dearbill/shared";
import { fetchDocuments } from "../sign/documents";

/**
 * The signable documents of the dashboard's latest enrollment, or an empty
 * list while loading, without an enrollment, or when the load fails: the
 * documents are an addition to Home, so a failure hides them rather than the
 * dashboard. A 401 calls `onSignOut`.
 *
 * A change of `enrollmentId`, including the null the dashboard passes while a
 * document's sign page is open, drops the loaded list, so the return to Home
 * refetches without showing a document as unsigned after its signature.
 */
export function useEnrollmentDocuments(
  enrollmentId: string | null,
  session: string,
  onSignOut: () => void,
): PortalSignableDocument[] {
  const [loaded, setLoaded] = useState<PortalSignableDocument[] | null>(null);
  const [loadedFor, setLoadedFor] = useState(enrollmentId);

  // Reset during render rather than in the effect, so the first render with
  // the new id already returns the empty list.
  if (loadedFor !== enrollmentId) {
    setLoadedFor(enrollmentId);
    setLoaded(null);
  }

  useEffect(() => {
    if (enrollmentId === null) return undefined;
    let cancelled = false;
    fetchDocuments(enrollmentId, session)
      .then((docs) => {
        if (!cancelled) setLoaded(docs);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) onSignOut();
        else setLoaded([]);
      });
    return () => {
      cancelled = true;
    };
  }, [enrollmentId, session, onSignOut]);

  return enrollmentId !== null && loaded !== null ? loaded : [];
}
